import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';

import { navigationService } from 'src/config/rootService';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { CountrySelectField, CountryOption } from 'src/shared/components/CountrySelect';
import { ConsentFormPageTestId } from './testIds';
import { DisclaimerModal } from 'src/shared/components/DisclaimerModal';
import { DeprecatedButtonGroup, Input, InputType } from '@modernhealth/vital-components';
import { isLastMandatoryTerm } from 'src/shared/helpers/isLastMandatoryTerm';
import { EuropeanCountryCodes } from 'src/shared/models/shared';
import { color, font } from 'src/shared/styles/variables';

import { ConsentTermsKorea } from '../containers/ConsentTermsKorea';
import { ConsentTermsMain } from '../containers/ConsentTermsMain';
import { ExternalLink } from 'src/shared/components/ExternalLink';

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});
const Title = styled.h1(
    {
        color: color.ebonyClay,
        lineHeight: '28px',
        textAlign: 'left',
        paddingBottom: '16px',
        margin: 0,
        width: '100%',
    },
    font.heavy(font.size.extraLarge),
);
const Subtitle = styled.h3(
    {
        color: color.blueBayoux,
        lineHeight: '24px',
        paddingBottom: '8px',
        marginBottom: 0,
        textAlign: 'left',
    },
    font.book(font.size.medium),
);

const SubtitleLink = styled(ExternalLink)({
    fontSize: 'inherit',
});

const CheckboxContainer = styled.div({
    width: '100%',
    paddingTop: '24px',
    paddingBottom: '40px',
});
const FormContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});
const InputContainer = styled.div({
    width: '100%',
    '& > *': {
        paddingTop: '16px',
    },
});
const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '110px',
    justifyContent: 'center',
    flexGrow: 1,
});
const ButtonsContainer = styled.div({
    width: '100%',
});

export enum Country {
    CHINA = 'China',
    SOUTH_KOREA = 'South Korea',
    US = 'United States',
}

export enum KoreaTerms {
    CONSENT_TO_ALL_ACCEPTED = 'consentToAllAccepted',
    TERMS = 'termsAccepted',
    PRIVACY = 'privacyAccepted',
    USE_SENSITIVE_INFO = 'collectAndUseSensitiveInfoAccepted',
    PROVISION_PERSONAL_INFO = 'provisionOfPersonalInfoUseAccepted',
    OVERSEAS_XFER_PERSONAL_INFO = 'overseasPersonalInfoXferAccepted',
    OPTIONAL_MARKETING_INFO_USE_ACCEPTED = 'collectPersonalInfoForMarketingAccepted',
}

type Props = RouteComponentProps<
    {},
    {},
    {
        token: string;
        companyCode: string;
        firstName: string;
        lastName: string;
        email: string;
        country: string;
        birthDate: string;
        uidb64: string;
        languagePreference: string;
        isSSOFlow: boolean;
    }
>;

export const ConsentFormPage: React.FC<Props> = ({ location }) => {
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [personalHealthAccepted, setPersonalHealthAccepted] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [hipaaAccepted, setHipaaAccepted] = useState(false);
    const [consentToAllAccepted, setConsentToAllAccepted] = useState(false);
    const [collectAndUseSensitiveInfoAccepted, setCollectAndUseSensitiveInfoAccepted] =
        useState(false);
    const [provisionOfPersonalInfoUseAccepted, setProvisionOfPersonalInfoUseAccepted] =
        useState(false);
    const [overseasPersonalInfoXferAccepted, setOverseasPersonalInfoXferAccepted] = useState(false);
    const [collectPersonalInfoForMarketingAccepted, setCollectPersonalInfoForMarketingAccepted] =
        useState(false);
    const [country, setCountry] = useState<CountryOption | null>(null);
    const [birthDate, setBirthDate] = useState(location.state?.birthDate || '');
    const [birthDateError, setBirthDateError] = useState('');
    const companyCode = location.state?.companyCode;
    const firstName = location.state?.firstName;
    const lastName = location.state?.lastName;
    const email = location.state?.email;
    const token = location.state?.token;
    const uidb64 = location.state?.uidb64 || undefined;
    const languagePreference = location.state?.languagePreference;
    const isSSOFlow = location.state?.isSSOFlow || false;
    const { t } = useTranslation();
    const { showKnTerms, hideTosPrivacyUpdateMemberWeb, showEuPersonalHealthInfoTerm } = useFlags();
    const isEuropeanCountry = !!country?.value && EuropeanCountryCodes[country.value] !== undefined;
    const isUS = !!country?.value && country.value === Country.US;
    const showAdditionalCheckboxesForKorea = country?.value === Country.SOUTH_KOREA && showKnTerms;

    const areTermsAccepted = () => {
        if (isEuropeanCountry && showEuPersonalHealthInfoTerm) {
            return termsAccepted && privacyAccepted && personalHealthAccepted;
        } else if (showAdditionalCheckboxesForKorea) {
            return areKnMandatoryTermsAccepted();
        } else if (isUS) {
            return termsAccepted && privacyAccepted && hipaaAccepted;
        } else {
            return termsAccepted && privacyAccepted;
        }
    };

    const isBirthDateValid = () => {
        // we only show the birthdate on this form for the magic link
        // we validate that by checking for the token
        if (token) {
            return birthDate && is18OrOlder();
        } else {
            return true;
        }
    };

    const isFormValid = () => {
        return (
            (hideTosPrivacyUpdateMemberWeb ? !termsAccepted : areTermsAccepted()) &&
            country &&
            isBirthDateValid()
        );
    };

    const is18OrOlder = () => {
        if (!birthDate) {
            return true;
        }

        return (
            moment().diff(
                moment(birthDate, 'YYYY-MM-DD').locale('en-US').format('YYYY-MM-DD'),
                'years',
            ) >= 18
        );
    };

    const toggleConsentToAll = () => {
        setConsentToAllAccepted(!consentToAllAccepted);
        setTermsAccepted(!consentToAllAccepted);
        setPrivacyAccepted(!consentToAllAccepted);
        setHipaaAccepted(!consentToAllAccepted);
        setCollectAndUseSensitiveInfoAccepted(!consentToAllAccepted);
        setProvisionOfPersonalInfoUseAccepted(!consentToAllAccepted);
        setOverseasPersonalInfoXferAccepted(!consentToAllAccepted);
        setCollectPersonalInfoForMarketingAccepted(!consentToAllAccepted);
    };

    const areKnMandatoryTermsAccepted = () => {
        return (
            termsAccepted &&
            privacyAccepted &&
            collectAndUseSensitiveInfoAccepted &&
            provisionOfPersonalInfoUseAccepted &&
            overseasPersonalInfoXferAccepted
        );
    };

    const handleAcceptOptionalMarketing = () => {
        setConsentToAllAccepted(
            areKnMandatoryTermsAccepted()
                ? !collectPersonalInfoForMarketingAccepted
                : consentToAllAccepted,
        );
        setCollectPersonalInfoForMarketingAccepted(!collectPersonalInfoForMarketingAccepted);
    };

    const toggleCheckbox = (type: string) => {
        switch (type) {
            case 'personalHealthAccepted':
                setPersonalHealthAccepted(!personalHealthAccepted);
                break;
            case 'privacyAccepted':
                setPrivacyAccepted(!privacyAccepted);
                break;
            case 'termsAccepted':
                setTermsAccepted(!termsAccepted);
                break;
            case 'hipaaAccepted':
                setHipaaAccepted(!hipaaAccepted);
                break;
            case 'consentToAllAccepted':
                setConsentToAllAccepted(!consentToAllAccepted);
                break;
            case 'collectAndUseSensitiveInfoAccepted':
                setCollectAndUseSensitiveInfoAccepted(!collectAndUseSensitiveInfoAccepted);
                break;
            case 'provisionOfPersonalInfoUseAccepted':
                setProvisionOfPersonalInfoUseAccepted(!provisionOfPersonalInfoUseAccepted);
                break;
            case 'overseasPersonalInfoXferAccepted':
                setOverseasPersonalInfoXferAccepted(!overseasPersonalInfoXferAccepted);
                break;
            case 'collectPersonalInfoForMarketingAccepted':
                setCollectPersonalInfoForMarketingAccepted(
                    !collectPersonalInfoForMarketingAccepted,
                );
                break;
            default:
                break;
        }
    };

    const updateTermAcceptance = (termName: string) => {
        switch (termName) {
            case KoreaTerms.CONSENT_TO_ALL_ACCEPTED:
                return toggleConsentToAll();
            case KoreaTerms.OPTIONAL_MARKETING_INFO_USE_ACCEPTED:
                return handleAcceptOptionalMarketing();
            default:
                return handleAcceptTerms(termName);
        }
    };

    const handleSubmit = () => {
        // EA China registors must accept disclaimer policies 04/13/21
        if (country?.value === Country.CHINA) {
            setShowDisclaimerModal(true);
        } else {
            navigateToPasswordFormPage();
        }
    };

    const handleGoBack = () => {
        if (token) {
            navigationService.navigate(`/registration?token=${token}`);
        } else if (uidb64) {
            navigationService.navigate(`/registration?uidb64=${uidb64}`);
        } else {
            const state = {
                birthDate,
                firstName,
                lastName,
                companyCode,
                email,
                languagePreference,
            };
            navigationService.navigate('/registration', state);
        }
    };

    const handleAutoToggle = (key: string) => {
        const hash = {
            termsAccepted,
            privacyAccepted,
            hipaaAccepted,
            collectAndUseSensitiveInfoAccepted,
            provisionOfPersonalInfoUseAccepted,
            overseasPersonalInfoXferAccepted,
        };
        const shouldIncludeConsentToAll =
            !consentToAllAccepted &&
            isLastMandatoryTerm(hash, key) &&
            collectPersonalInfoForMarketingAccepted;
        const shouldIncludeOptional =
            consentToAllAccepted &&
            isLastMandatoryTerm(hash, key) &&
            !collectPersonalInfoForMarketingAccepted;
        return {
            shouldIncludeConsentToAll,
            shouldIncludeOptional,
        };
    };

    const handleAcceptTerms = (key: string) => {
        const { shouldIncludeConsentToAll, shouldIncludeOptional } = handleAutoToggle(key);
        setConsentToAllAccepted(shouldIncludeConsentToAll ? true : false);

        toggleCheckbox(key);
        setCollectPersonalInfoForMarketingAccepted(
            shouldIncludeOptional
                ? !collectPersonalInfoForMarketingAccepted
                : collectPersonalInfoForMarketingAccepted,
        );
    };

    const navigateToPasswordFormPage = () => {
        if (isSSOFlow) {
            navigationService.navigate('/preferred-email');
        } else {
            const terms = {
                personalHealthAccepted,
                consentToAllAccepted,
                termsAccepted,
                privacyAccepted,
                hipaaAccepted,
                collectAndUseSensitiveInfoAccepted,
                provisionOfPersonalInfoUseAccepted,
                overseasPersonalInfoXferAccepted,
                collectPersonalInfoForMarketingAccepted,
            };
            const state = {
                firstName,
                lastName,
                email,
                token,
                companyCode,
                birthDate,
                terms,
                country: country?.value,
                uidb64,
                languagePreference,
            };
            navigationService.navigate('/password-form-page', state);
        }
    };

    useEffect(() => {
        if (is18OrOlder()) {
            setBirthDateError('');
        } else {
            setBirthDateError(t('auth:registration_form.errors.underaged'));
        }
    }, [birthDate]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <FormContainer>
                    <Title>{t('auth:consent_form_page.title')}</Title>
                    <Subtitle>
                        <Trans
                            i18nKey="consent_form_page.description"
                            ns="auth"
                            components={{
                                'privacy-policy': (
                                    <SubtitleLink href="https://www.modernhealth.com/privacy-notice" />
                                ),
                            }}
                        />
                    </Subtitle>
                    <InputContainer>
                        <CountrySelectField
                            fieldId="country"
                            testID={ConsentFormPageTestId.CountryField}
                            value={country?.value}
                            label={t('auth:consent_form_page.label.country_of_residence')}
                            onChange={setCountry}
                        />
                        {/* Dependents don't have to input the age, we get it from owner dependent invitation */}
                        {token && (
                            <Input
                                fieldId="date"
                                testID={ConsentFormPageTestId.DateField}
                                type={InputType.DatePicker}
                                initialValue={birthDate ?? ''}
                                label={t('auth:consent_form_page.label.date_of_birth')}
                                onChange={value => setBirthDate(value.toString())}
                                error={birthDateError}
                            />
                        )}
                    </InputContainer>
                    <CheckboxContainer data-testid="ConsentContainerTestId">
                        {!showAdditionalCheckboxesForKorea && (
                            <ConsentTermsMain
                                hideTosPrivacyUpdateMemberWeb={hideTosPrivacyUpdateMemberWeb}
                                showEuPersonalHealthInfoTerm={showEuPersonalHealthInfoTerm}
                                isEuropeanCountry={isEuropeanCountry}
                                isUS={isUS}
                                onChange={toggleCheckbox}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                hipaaAccepted={hipaaAccepted}
                                personalHealthAccepted={personalHealthAccepted}
                            />
                        )}

                        {showAdditionalCheckboxesForKorea && (
                            <ConsentTermsKorea
                                onChange={updateTermAcceptance}
                                consentToAllAccepted={consentToAllAccepted}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                collectAndUseSensitiveInfoAccepted={
                                    collectAndUseSensitiveInfoAccepted
                                }
                                provisionOfPersonalInfoUseAccepted={
                                    provisionOfPersonalInfoUseAccepted
                                }
                                overseasPersonalInfoXferAccepted={overseasPersonalInfoXferAccepted}
                                collectPersonalInfoForMarketingAccepted={
                                    collectPersonalInfoForMarketingAccepted
                                }
                            />
                        )}
                    </CheckboxContainer>
                    <ButtonsContainer>
                        <DeprecatedButtonGroup
                            onFirstButtonClick={handleGoBack}
                            onSecondButtonClick={handleSubmit}
                            secondButtonDisabled={!isFormValid()}
                            firstButtonText={t('auth:consent_form_page.back')}
                            secondButtonText={t('auth:consent_form_page.continue')}
                        ></DeprecatedButtonGroup>
                    </ButtonsContainer>
                </FormContainer>
            </Container>
            <DisclaimerModal
                visible={showDisclaimerModal}
                description={t('shared:disclaimer_modal.description', {
                    returnObjects: true,
                })}
                onClick={navigateToPasswordFormPage}
                buttonText={t('shared:disclaimer_modal.buttonText')}
            />
        </>
    );
};
